import * as React from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Button} from 'shells/button/button';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';

import type {
    UnpauseDripSequence as ResumeDripSequenceMutation,
    UnpauseDripSequenceVariables as ResumeDripSequenceMutationVariables,
    DripSequenceFragment as DripSequence,
    SmsDripSequenceFragment as SmsDripSequence,
} from 'nutshell-graphql-types';

import DRIP_SEQUENCE_UNPAUSE from './graphql/mutations/drip-sequence-unpause.graphql';

type Props = {
    disabled: boolean;
    dripSequence: SmsDripSequence | DripSequence;
    onResumed: () => void;
};

export function ResumeSequenceButton(props: Props) {
    const [isResumingSequence, setIsResumingSequence] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);

    const [resumeDripSequence] = useMutation<
        ResumeDripSequenceMutation,
        ResumeDripSequenceMutationVariables
    >(DRIP_SEQUENCE_UNPAUSE);

    return (
        <>
            <Button
                disabled={props.disabled}
                variant='primary'
                size='big'
                onClick={() => {
                    setErrorMessage(undefined);
                    setIsResumingSequence(true);
                }}
            >
                Resume sequence
            </Button>
            {isResumingSequence ? (
                <ConfirmationDialog
                    title='Resume sending'
                    message={`Are you ready to resume sending this drip? Current recipients will begin receiving messages again${
                        props.dripSequence.startTrigger
                            ? ' and the start trigger will be reactivated.'
                            : '.'
                    }`}
                    confirmText='Yes, resume'
                    cancelText='No, don’t resume'
                    isConfirmDestructive={false}
                    onCancel={() => {
                        setErrorMessage(undefined);
                        setIsResumingSequence(false);
                    }}
                    onConfirm={() => {
                        setErrorMessage(undefined);
                        setIsSaving(true);

                        return resumeDripSequence({
                            variables: {
                                input: {
                                    dripSequenceId: props.dripSequence.id,
                                },
                            },
                        })
                            .then(() => {
                                setIsSaving(false);

                                // Timeout just keeps dialog open a little longer to see the save is confirmed
                                setTimeout(() => {
                                    props.onResumed();
                                    setIsResumingSequence(false);
                                }, 1000);
                            })
                            .catch((error) => {
                                setIsSaving(false);
                                setErrorMessage(
                                    safelyGetGraphQLErrorMessage(error) ||
                                        'Unable to resume this drip sequence.'
                                );
                            });
                    }}
                    isSaving={isSaving}
                    errorMessage={errorMessage}
                    customSaveText={{
                        initial: 'Yes, resume',
                        saving: 'Resuming…',
                        complete: 'Resumed',
                    }}
                />
            ) : undefined}
        </>
    );
}
